import type { MouseEvent } from 'react';
import { Menu } from 'lucide-react';
import { cn } from '@eombor/ui';
import { t } from '../../shared/i18n/index.js';
import { activeNavTo, isPlainLeftClick, navEntry, visibleNavItems } from './nav-model.js';

/**
 * Bitta tugmaning ko'rinishi — havola ham, «Menyu» ham bir xil chiziladi.
 *
 * Ular BOSHQA-BOSHQA elementlar (`<a>` va `<button>`): birinchisi manzilga
 * olib boradi va yangi oynada ochilishi kerak, ikkinchisi manzilsiz. Lekin
 * ko'zga bir xil qator bo'lib ko'rinishi shart.
 */
const tabClass = (active: boolean): string =>
  cn(
    'flex h-full w-full flex-col items-center justify-center gap-1 px-1',
    'text-[0.6875rem] font-medium leading-none',
    'transition-[color] duration-[var(--motion-fast)] ease-[var(--ease-standard)]',
    active ? 'text-accent' : 'text-text-faint',
  );

/**
 * Qatorga sig'adigan bo'limlar soni.
 *
 * To'rtta + «Menyu» = beshta tugma. Undan ko'pi 360px lik ekranda tugma
 * enini bosh barmoq nishoniga yetmaydigan darajada toraytiradi.
 */
export const TAB_COUNT = 4;

export interface MobileTabBarProps {
  /** Ruxsat tekshiruvi — odatda `usePermission()` dan keladi. */
  can: (permission: string) => boolean;
  /** Joriy manzil; berilmasa hech bir element yonmaydi. */
  activePath?: string;
  /** Bo'lim yonidagi son. Kalit — `NavItem.to`; nol bo'lsa chizilmaydi. */
  badges?: Readonly<Record<string, number>>;
  /** Xodimga bo'lim biriktirilganmi — «Mening omborim» shunga qarab chiziladi. */
  hasSubdivision?: boolean;
  /** Berilsa — bosilganda sahifa qayta yuklanmaydi (SPA o'tishi). */
  onNavigate?: (to: string) => void;
  /** Qolgan bo'limlar ro'yxatini ochadi. */
  onOpenMenu?: () => void;
}

/**
 * Telefondagi pastki navigatsiya qatori.
 *
 * Bo'limlar `NAV_ITEMS` TARTIBIDAN olinadi: ro'yxat ish oqimi bo'yicha
 * tuzilgan, shuning uchun birinchi to'rttasi har rol uchun eng ko'p
 * ochiladigan bo'lim bo'lib chiqadi — alohida "mobil" ro'yxat saqlash
 * shart emas va u eskirib qolmaydi.
 */
export const MobileTabBar = ({
  can,
  activePath = '',
  badges = {},
  hasSubdivision = false,
  onNavigate,
  onOpenMenu,
}: MobileTabBarProps) => {
  const visible = visibleNavItems(can, hasSubdivision);
  const tabs = visible.slice(0, TAB_COUNT);

  /* Faol bo'lim BUTUN ro'yxatdan qidiriladi, faqat qatordagi to'rttadan
     emas: javob "qaysi tugma yonadi" degan savolga emas, "odam qayerda
     turibdi" degan savolga kerak. */
  const activeTo = activeNavTo(visible, activePath);

  /* Joriy bo'lim qatorga sig'masa — «Menyu» yonadi: pastda hech narsa
     yonmagan qator odamni "qayerdaman?" degan savol bilan qoldiradi. */
  const menuActive = activeTo !== undefined && !tabs.some((item) => item.to === activeTo);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>, to: string): void => {
    if (onNavigate === undefined || !isPlainLeftClick(event)) return;
    event.preventDefault();
    onNavigate(to);
  };

  return (
    <nav
      aria-label={t('tabbar.label')}
      className={cn(
        // Pastki qator ham KARKAS: sahifadagi suzuvchi panel uni
        // to'sib qo'ymasligi kerak. Panellar esa o'z navbatida uning
        // ustidan emas, USTIDA — ya'ni tabbar balandligicha yuqoridan
        // boshlanadi (`--size-tabbar` bilan).
        'fixed inset-x-0 bottom-0 z-[var(--z-chrome)]',
        'border-t border-border bg-surface/95 backdrop-blur-md',
        // Xavfsiz zona — iPhone dagi pastki chiziq tugmalarni yopmasin.
        'pb-[env(safe-area-inset-bottom)]',
      )}
    >
      <ul className="flex h-[var(--size-tabbar)] items-stretch">
        {tabs.map((item) => {
          const label = t(item.label);
          const count = badges[item.to] ?? 0;
          const Icon = item.icon;
          const active = item.to === activeTo;

          return (
            <li key={item.to} className="min-w-0 flex-1">
              <a
                href={navEntry(item)}
                // Son NOMGA qo'shiladi: qizil doiracha faqat ko'z uchun,
                // ekran o'qigich uchun esa u umuman yo'q.
                aria-label={count > 0 ? `${label} (${count})` : label}
                aria-current={active ? 'page' : undefined}
                onClick={(event) => handleClick(event, navEntry(item))}
                className={tabClass(active)}
              >
                <span className="relative flex items-center">
                  <Icon size={20} aria-hidden />
                  {count > 0 ? (
                    <span
                      aria-hidden
                      className={cn(
                        'absolute -top-0.5 -right-1.5 size-2 rounded-[var(--radius-pill)]',
                        'bg-danger ring-2 ring-surface',
                      )}
                    />
                  ) : null}
                </span>
                {/* Nom bitta qatorda qoladi: ikki qatorli yorliq qator
                    balandligini o'zgartirib, tugmalarni tekislikdan chiqaradi. */}
                <span className="w-full truncate text-center" title={label}>
                  {t(item.shortLabel ?? item.label)}
                </span>
              </a>
            </li>
          );
        })}

        <li className="min-w-0 flex-1">
          <button
            type="button"
            onClick={onOpenMenu}
            aria-current={menuActive ? 'page' : undefined}
            className={tabClass(menuActive)}
          >
            <Menu size={20} aria-hidden />
            <span className="w-full truncate text-center">{t('tabbar.more')}</span>
          </button>
        </li>
      </ul>
    </nav>
  );
};
